import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Hexagon, Swords, Brain, TrendingUp, ScanLine, ArrowRight, Sparkles } from "lucide-react";
import Orb from "@/components/Orb";
import InstallButton from "@/components/InstallButton";
import { useAuth } from "@/context/AuthContext";

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const steps = [
    { icon: ScanLine, title: "The Awakening", desc: "ARCHON interviews you, reading your strengths, instincts and flaws." },
    { icon: Hexagon, title: "System Unlock", desc: "When enough is known, your personal System page is revealed." },
    { icon: Swords, title: "Your Build", desc: "An RPG class, custom skills, passives and weaknesses — uniquely yours." },
    { icon: TrendingUp, title: "Level Up IRL", desc: "Complete daily training, earn XP, climb ranks E → S, unlock the skill tree." },
  ];

  return (
    <div className="min-h-screen relative overflow-hidden grid-bg">
      <div className="absolute inset-0 glow-radial pointer-events-none" />

      <nav className="relative z-10 flex items-center justify-between px-6 sm:px-10 py-6">
        <div className="flex items-center gap-3" data-testid="brand">
          <Hexagon className="text-cyan-400" size={22} />
          <span className="font-display text-xl font-bold tracking-[0.3em]">ARCHON</span>
        </div>
        <div className="flex items-center gap-3">
          <InstallButton />
          <button
            data-testid="nav-launch-btn"
            onClick={() => navigate(user ? "/hub" : "/auth")}
            className="mono-label border border-blue-500/40 px-4 py-2 rounded-full hover:shadow-[0_0_20px_rgba(59,130,246,0.4)] transition-all duration-300"
          >
            {user ? "Enter System" : "Sign In"}
          </button>
        </div>
      </nav>

      <div className="relative z-10 max-w-6xl mx-auto px-6 pt-10 sm:pt-14 pb-24 flex flex-col items-center text-center">
        <motion.div initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }}>
          <Orb state="idle" size={200} />
        </motion.div>

        <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mono-label mt-10">
          [ SYSTEM ONLINE ] — YOU HAVE BEEN SELECTED
        </motion.p>
        <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
          className="font-display text-4xl sm:text-6xl font-extrabold tracking-tight mt-4 max-w-3xl">
          Turn your life into <span className="text-cyan-400">a game worth winning.</span>
        </motion.h1>
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
          className="text-slate-400 mt-6 max-w-xl text-lg">
          ARCHON is a System intelligence that awakens your hidden build — assigning you an RPG class, custom skills,
          and a real-world leveling path. Talk to it. Get read. Rise through the ranks.
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
          className="flex flex-col sm:flex-row gap-4 mt-10">
          <button
            data-testid="begin-awakening-btn"
            onClick={() => navigate(user ? "/hub" : "/auth")}
            className="group flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-8 py-4 rounded-full font-medium transition-all duration-300 hover:shadow-[0_0_30px_rgba(59,130,246,0.5)] hover:-translate-y-0.5"
          >
            <Sparkles size={18} /> Begin the Awakening
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-24 w-full">
          {steps.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 + i * 0.1 }}
              className="glass rounded-xl p-6 text-left hover:border-blue-500/50 transition-all duration-300" data-testid={`step-${i}`}>
              <div className="flex items-center gap-2 mb-4">
                <span className="mono-label">0{i + 1}</span>
                <f.icon className="text-cyan-400" size={22} />
              </div>
              <h3 className="font-display font-semibold text-lg">{f.title}</h3>
              <p className="text-slate-400 text-sm mt-2 leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
